import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus } = FiIcons;

export default function Audit() {
  const [activeFaq, setActiveFaq] = useState(null);

  const pageStyle = {
    fontFamily: 'Arial, Helvetica, sans-serif'
  };

  const scrollToAudit = () => {
    const element = document.getElementById('start-your-audit');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const faqs = [
    {
      q: "Do you need access to my accounts?",
      a: "No. Just public URLs. Nothing behind a password. If you can see it from Google, I can audit it."
    },
    {
      q: "What if I don't have a website?",
      a: "I still audit your Google Business Profile, your Instagram, your Facebook, and anywhere else you show up online. Most of your leads are coming from those anyway."
    },
    {
      q: "What exactly do you audit?",
      a: "Your website on any platform. Your Google Business Profile. Your Instagram. Your Facebook. A live Google search for \"dog walker [your city].\" Your booking flow tested from the outside the way a new client would see it. Your branding consistency, your calls to action, and how you are asking for reviews."
    },
    {
      q: "Can I share the video with my team or business partner?",
      a: "Yes. The video link is yours. Share it with anyone who helps you run the business."
    },
    {
      q: "How do I know you are legit?",
      a: "Read the Onyx Pet Care case study linked higher on this page. Real client. Real business. Real results. Verifiable numbers."
    },
    {
      q: "What if my audit is bad or I don't like it?",
      a: "See the guarantee above. If I don't find 3 specific fixes you can ship this week, the audit is free. That is not marketing language. That is the actual deal."
    },
    {
      q: "How fast is 72 hours really?",
      a: "72 hours from the moment your form and payment come in. Not business days. Actual hours."
    },
    {
      q: "Can we hop on a call to go over the video?",
      a: "Not included in the audit. If you want a live walkthrough of how to implement the fixes, book a Next Steps call after you watch your video. Link comes with the delivery email."
    }
  ];

  return (
    <>
      <SEO 
        title="The Audit // Otso Co" 
        description="A 72 hour video audit that tells you exactly what is broken in your dog walking business funnel. And how to fix it this week." 
        path="/audit" 
      />

      <div style={pageStyle} className="min-h-screen text-brand-primaryDark">
        
        {/* SECTION 1: HERO (SALMON) */}
        <section className="bg-brand-salmon py-32 px-6 text-center">
          <div className="max-w-5xl mx-auto">
            <div className="text-brand-primaryDark text-sm font-bold tracking-[0.2em] uppercase mb-8">
              THE AUDIT // FOR DOG WALKERS
            </div>
            <h1 className="text-brand-cream text-4xl md:text-6xl font-bold mb-10 leading-tight">
              A 15 minute video that tells you what is broken in your funnel. Delivered in 72 hours.
            </h1>
            <p className="text-2xl md:text-3xl text-brand-primaryDark font-bold max-w-4xl mx-auto leading-tight mb-12">
              Built for dog walkers. $97 for the first 5 founding clients. Then $147. If I don't find at least 3 specific fixes you can ship this week, the audit is free.
            </p>
            <button 
              onClick={scrollToAudit}
              className="bg-brand-salmon border-2 border-brand-primaryDark text-brand-primaryDark px-12 py-5 rounded-full font-bold text-xl transition-all hover:bg-brand-primaryDark hover:text-white"
            >
              Start my audit
            </button>
          </div>
        </section>

        {/* SECTION 2: PROOF (NAVY) */}
        <section className="bg-brand-primaryDark py-24 px-6 text-center text-brand-cream">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-10">
              The last dog walker who did this got real results
            </h2>
            <p className="text-xl md:text-2xl leading-relaxed mb-12 max-w-[720px] mx-auto">
              Gee Centeno owns Onyx Pet Care in Laurel, Maryland and has been in business since 2012. Her website had under 75 views a month. Google did not return her business for "dog walker Laurel MD." In 90 days of working the same funnel-first diagnostic behind this audit, Gee went from 4 Google reviews to 15, her Google visibility lifted 100%, and her website views grew 230%. No ads. No budget.
            </p>
            <Link 
              to="/onyx-case-study" 
              className="inline-block bg-brand-salmon text-brand-primaryDark px-12 py-5 rounded-full font-bold text-xl transition-all hover:bg-white"
            >
              Read the Onyx Pet Care case study
            </Link>
          </div>
        </section>

        {/* SECTION 3: WHAT YOU GET / HOW IT WORKS (CREAM) */}
        <section className="bg-brand-cream py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-20 text-center">
              What you get and how it works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  n: "01",
                  h: "Tell me about your business",
                  b: "Fill out the form below. 6 short questions. No account logins. No access tokens. Just the public links I need to do my work. Pay the same checkout. Takes under 2 minutes."
                },
                {
                  n: "02",
                  h: "I audit your funnel",
                  b: "I record a 15 to 20 minute video walking through your website, your Google Business Profile, your Instagram, your Facebook, and a live Google search for \"dog walker [your city].\" I tell you what is broken, what is weak, and what is quietly costing you new clients every month."
                },
                {
                  n: "03",
                  h: "You get the video in 72 hours",
                  b: "A private video link and a one page PDF action list land in your email within 72 hours. 3 to 5 specific fixes ranked by impact. What to ship this week. What can wait. No live call. No calendar juggling."
                }
              ].map((card, i) => (
                <div key={i} className="bg-brand-blush p-8 rounded-[24px] shadow-sm flex flex-col items-center md:items-start">
                  <div className="w-14 h-14 bg-brand-salmon text-brand-primaryDark rounded-2xl flex items-center justify-center text-2xl font-bold mb-8">
                    {card.n}
                  </div>
                  <h3 className="text-xl font-bold mb-4">{card.h}</h3>
                  <p className="leading-relaxed text-gray-800">{card.b}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4: GUARANTEE (SALMON STRIP) */}
        <section className="bg-brand-salmon py-16 px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">
              If I don't find 3 specific fixes you can ship this week, the audit is free.
            </h2>
            <p className="text-lg font-bold max-w-[640px] mx-auto">
              Every dog walker site I have looked at has at least 3 things a busy owner can fix in one week. If yours is somehow the exception, I refund every dollar and you keep the video.
            </p>
          </div>
        </section>

        {/* SECTION 5: START YOUR AUDIT (BLUSH) */}
        <section id="start-your-audit" className="bg-brand-blush py-24 px-6 text-center">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Start your audit</h2>
            <p className="text-lg font-bold mb-12 max-w-[640px] mx-auto">
              Takes less than 2 minutes. Intake questions and payment happen in the same checkout. $97 for the first 5 founding clients. $147 after. Your video lands in your email within 72 hours.
            </p>
            <div className="bg-white rounded-[32px] shadow-2xl overflow-hidden mb-8">
              <iframe
                style={{ border: 'none', width: '100%', height: '100vh', minHeight: '900px' }}
                src="https://client.otsoco.com/catalog/audit-1776571706803468"
                title="Audit Checkout"
              ></iframe>
            </div>
            <p className="italic text-brand-primaryDark text-sm">
              Secure checkout powered by Time To Pet. Founding price active until the first 5 slots are filled.
            </p>
          </div>
        </section>

        {/* SECTION 6: FAQ (CREAM) */}
        <section className="bg-brand-cream py-24 px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Common questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">
                  <button 
                    onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                    className="w-full flex justify-between items-center p-6 text-left font-bold"
                  >
                    {faq.q}
                    <SafeIcon icon={FiPlus} className={`transition-transform duration-200 ${activeFaq === i ? 'rotate-45' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {activeFaq === i && (
                      <motion.div 
                        initial={{ height: 0, opacity: 0 }} 
                        animate={{ height: 'auto', opacity: 1 }} 
                        exit={{ height: 0, opacity: 0 }}
                      >
                        <div className="p-6 pt-0 text-gray-600 border-t border-gray-50 leading-relaxed">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </>
  );
}